<template>
  <SoSkeleton v-if="isLoading" type="card" />
  <div v-else>
    <div v-if="hasMultiplePendencies">
      <p class="font-weight-medium mb-8">
        Please resolve these checks to proceed with refund.
      </p>

      <p class="mb-8">
        *Customer might be
        <span class="font-weight-medium">
          eligible to a refund up to £100.00.
        </span>
        This amount may change after resolving the pendencies on the account.
      </p>
    </div>

    <div v-else-if="noRecentReadings || hasPartialRecentReadings">
      <p class="mb-8">
        Please make sure you have resolved any billing issues on the account
        before attempting to process a refund.
      </p>
      <p>
        <span class="font-weight-medium">
          Customer might be eligible for a refund of up to £100.00.
        </span>
        This amount might change once any billing or meter read issues are
        resolved.
      </p>
    </div>

    <div v-else-if="noRecentBills || paymentIsLessThanSuggested">
      <p class="mb-8">
        We're happy to proceed with a refund, but this account has unresolved
        pendencies that could lead the customer to fall into debt in the future.
        <span class="font-weight-medium">
          Please resolve these checks to proceed with refund.
        </span>
      </p>
      <p>
        *Customer might be
        <span class="font-weight-medium">
          eligible for a refund of up to £100.00.
        </span>
        This amount might change once any billing or meter read issues are
        resolved.
      </p>
    </div>

    <SoAlert v-if="hasPartialRecentReadings" class="mt-4">
      <div class="d-flex">
        <span>No readings in the last 32 days</span>
        <SoStatus
          :status="new Status('caution', 'To Resolve')"
          class="ml-auto"
        />
      </div>
    </SoAlert>

    <SoAlert v-if="noRecentReadings" class="mt-4">
      <div class="d-flex">
        <span>No readings in the last 60 days</span>
        <SoStatus
          :status="new Status('caution', 'To Resolve')"
          class="ml-auto"
        />
      </div>
    </SoAlert>

    <SoAlert v-if="noRecentBills" class="mt-4">
      <div class="d-flex">
        <span>No bill in the last 2 months</span>
        <SoStatus
          :status="new Status('caution', 'To Resolve')"
          class="ml-auto"
        />
      </div>
    </SoAlert>

    <SoAlert v-if="paymentIsLessThanSuggested" class="mt-4">
      <div class="d-flex">
        <div>
          <p class="mb-2">Payment amount lower than suggested</p>
          <p class="mb-2">
            This customer is
            <span class="font-weight-medium">paying {{ paymentAmount }}</span>
            and should be paying
            <span class="font-weight-medium">{{ suggestedAmount }}.</span>
            Creating a refund without updating the payment schedule might create
            debt in the future.
          </p>
        </div>
        <SoStatus
          :status="new Status('caution', 'To Resolve')"
          class="ml-auto"
        />
      </div>
    </SoAlert>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { getRefundEligibility } from "@/services-handlers/getRefundEligibility"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import { Status } from "@/types/Status"
  import SoAlert from "@/components/SoAlert.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"

  export default defineComponent({
    components: {
      SoAlert,
      SoStatus,
      SoSkeleton,
    },
    props: {
      noRecentReadings: {
        type: Boolean,
        default: false,
      },
      hasPartialRecentReadings: {
        type: Boolean,
        default: false,
      },
      noRecentBills: {
        type: Boolean,
        default: false,
      },
      accountInDebit: {
        type: Boolean,
        default: false,
      },
      paymentIsLessThanSuggested: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        Status,
        current: useCurrentAccountStore(),
      }
    },
    computed: {
      isLoading() {
        return getRefundEligibility(this.current.accountNumber).isFetching
      },
      hasMultiplePendencies() {
        return (
          (this.noRecentReadings || this.hasPartialRecentReadings) &&
          this.noRecentBills
        )
      },
      paymentAmount() {
        return formatMoney({
          amount:
            this.current.nextPaymentSchedule.data?.nextPayment ??
            this.current.currentPaymentSchedule.data?.nextPayment ??
            0,
        })
      },
      suggestedAmount() {
        return formatMoney({
          amount: this.current.suggestedPayment(true, true).data,
        })
      },
    },
  })
</script>
